import { useEffect, useRef } from 'react';

const problems = [
  {
    id: '01',
    icon: 'ri-phone-line',
    statement: 'Missed calls become lost revenue — with no system to recover them.',
  },
  {
    id: '02',
    icon: 'ri-flow-chart',
    statement: 'Decisions are delayed by fragmented data across disconnected platforms.',
  },
  {
    id: '03',
    icon: 'ri-alert-line',
    statement: 'Compliance exposure grows when intake and documentation lag behind operations.',
  },
  {
    id: '04',
    icon: 'ri-time-line',
    statement: 'Lead pipelines stall because follow-up is manual, inconsistent, and slow.',
  },
  {
    id: '05',
    icon: 'ri-wifi-off-line',
    statement: 'Field-to-office communication breaks down, delaying project delivery.',
  },
  {
    id: '06',
    icon: 'ri-money-dollar-circle-line',
    statement: 'Revenue opportunities are abandoned when qualification processes are undefined.',
  },
];

export default function ProblemSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const items = entry.target.querySelectorAll('[data-animate]');
            items.forEach((item, i) => {
              setTimeout(() => {
                (item as HTMLElement).style.opacity = '1';
                (item as HTMLElement).style.transform = 'translateY(0)';
              }, i * 90);
            });
          }
        });
      },
      { threshold: 0.08 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="problem" className="bg-[#FBFBFD] dark:bg-[#1A1A1D] py-28 md:py-40">
      <div className="max-w-7xl mx-auto px-6 md:px-12" ref={sectionRef}>
        {/* Header */}
        <div className="max-w-3xl mx-auto text-center mb-20">
          <div className="inline-flex items-center gap-3 mb-6">
            <span className="w-8 h-px bg-[#5BBFB5]" />
            <span className="text-[#C9A840] text-xs font-semibold" style={{ letterSpacing: '0.22em', textTransform: 'uppercase' }}>
              The Operational Gap
            </span>
            <span className="w-8 h-px bg-[#5BBFB5]" />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-[#1D1D1F] dark:text-[#F2F0EC] tracking-tight leading-tight mb-6">
            The Hidden Cost of<br />Misaligned Systems
          </h2>
          <p className="text-[#6E6E73] dark:text-[#96969E] text-lg leading-relaxed">
            High-performance organizations lose ground not through strategy failures,
            but through operational gaps that compound silently until the cost is measurable.
          </p>
        </div>

        {/* Problem grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {problems.map((p, i) => (
            <div
              key={p.id}
              data-animate
              style={{
                opacity: 0,
                transform: 'translateY(20px)',
                transition: `opacity 0.6s ease ${i * 0.08}s, transform 0.6s ease ${i * 0.08}s`,
              }}
              className="group bg-white dark:bg-[#0B0B0C] border border-[#E8E8ED] dark:border-[#252528] p-8 hover:border-[#C9A840]/40 dark:hover:border-[#C9A840]/30 transition-all duration-300 relative overflow-hidden cursor-default"
            >
              {/* Hover gold top line */}
              <div className="absolute top-0 left-0 w-full h-[2px] bg-[#C9A840] scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />

              <div className="flex items-start gap-5">
                <div className="w-10 h-10 flex items-center justify-center border border-[#E8E8ED] dark:border-[#252528] group-hover:border-[#C9A840]/50 flex-shrink-0 transition-colors duration-300 mt-0.5">
                  <i className={`${p.icon} text-[#C9A840] text-base`}></i>
                </div>
                <div>
                  <span className="block text-[#C9A840]/50 text-xs font-semibold mb-3" style={{ letterSpacing: '0.2em' }}>
                    {p.id}
                  </span>
                  <p className="text-[#6E6E73] dark:text-[#A8A8B2] text-sm leading-relaxed group-hover:text-[#1D1D1F] dark:group-hover:text-[#F2F0EC] transition-colors duration-300">
                    {p.statement}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
